import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Chip, IconButton, Alert } from '@mui/material';
import { Add, Edit, Delete, People } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function UsuariosPage() {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', password: '', full_name: '', role: 'view', expires_at: '' });
  const [error, setError] = useState('');

  const loadUsuarios = async () => {
    try {
      const res = await api.get('/usuarios');
      setUsuarios(res.data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadUsuarios(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ username: '', email: '', password: '', full_name: '', role: 'view', expires_at: '' });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      username: u.username, email: u.email, password: '',
      full_name: u.full_name || '', role: u.role,
      expires_at: u.expires_at ? u.expires_at.split('T')[0] : ''
    });
    setDialogOpen(true);
  };

  const save = async () => {
    setError('');
    try {
      if (editing) {
        await api.put(`/usuarios/${editing.id}`, {
          full_name: form.full_name, email: form.email, role: form.role,
          expires_at: form.expires_at || null
        });
      } else {
        await api.post('/usuarios', form);
      }
      setDialogOpen(false);
      loadUsuarios();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const deactivate = async (u) => {
    if (!confirm(`¿Desactivar usuario ${u.username}?`)) return;
    try {
      await api.delete(`/usuarios/${u.id}`);
      loadUsuarios();
    } catch (err) { console.error(err); }
  };

  const roleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'operator': return 'primary';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <People color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Gestión de Usuarios</Typography>
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openNew}>Nuevo Usuario</Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Rol</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Expira</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usuarios.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.full_name || '-'}</TableCell>
                    <TableCell><Chip label={u.role} color={roleColor(u.role)} size="small" sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell>{u.expires_at ? new Date(u.expires_at).toLocaleDateString('es-CL') : 'Sin expiración'}</TableCell>
                    <TableCell><Chip label={u.is_active ? 'Activo' : 'Inactivo'} color={u.is_active ? 'success' : 'default'} size="small" /></TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openEdit(u)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deactivate(u)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField fullWidth label="Nombre de usuario" value={form.username}
            onChange={e => setForm({ ...form, username: e.target.value })}
            disabled={!!editing} sx={{ mb: 2, mt: 1 }} />
          <TextField fullWidth label="Email" value={form.email} type="email"
            onChange={e => setForm({ ...form, email: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Nombre completo" value={form.full_name}
            onChange={e => setForm({ ...form, full_name: e.target.value })} sx={{ mb: 2 }} />
          {!editing && (
            <TextField fullWidth label="Contraseña" type="password" value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })} sx={{ mb: 2 }} />
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Rol</InputLabel>
            <Select value={form.role} label="Rol" onChange={e => setForm({ ...form, role: e.target.value })}>
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="operator">Operador</MenuItem>
              <MenuItem value="view">Visualizador</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth type="date" label="Fecha de expiración"
            value={form.expires_at} InputLabelProps={{ shrink: true }}
            onChange={e => setForm({ ...form, expires_at: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Guardar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
